import { DOCUMENT_MAX_SIZE_BYTES, VALUATION_REPORT_ALLOWED_CONTENT_TYPES } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

// The chosen file is not a permitted valuation report (wrong type or too large).
// Caught before any request so nothing is stored for it and the request's status
// stays untouched (AC 44). The client check is a convenience — the media edge
// policy + the confirm leaf remain the authoritative server-side gate.
export class DocumentPolicyError extends Error {}

// The presign / direct-to-S3 upload / confirm round-trip failed.
export class DocumentUploadError extends Error {}

// The valuation report policy is `VALUATION_REPORT_ALLOWED_CONTENT_TYPES`, not the
// property-document one: HTML is a report type and nothing else (IMM-475.1). The
// operator picks per KIND, so each slot carries only the types its kind accepts —
// otherwise the HTML slot would reject its own file client-side.
export const VALUATION_REPORT_HTML_CONTENT_TYPES: ReadonlyArray<string> = ['text/html'];

export const VALUATION_REPORT_DOCUMENT_CONTENT_TYPES: ReadonlyArray<string> = (
  VALUATION_REPORT_ALLOWED_CONTENT_TYPES as ReadonlyArray<string>
).filter((contentType) => !VALUATION_REPORT_HTML_CONTENT_TYPES.includes(contentType));

// Client-side mirror of the report policy, read from the contract constants so it
// never drifts from the server gate. `accepted` is the slot's own set.
export function isAllowedReport(file: File, accepted: ReadonlyArray<string>): boolean {
  return accepted.includes(file.type) && file.size <= DOCUMENT_MAX_SIZE_BYTES;
}

/**
 * How long the whole presign → store → confirm round-trip may take before it is
 * abandoned. A stalled transfer is not a rejection — without this the promise
 * stays pending forever and every in-flight flag behind it latches (IMM-822).
 * 120 s is chosen against the 20 MB ceiling (`DOCUMENT_MAX_SIZE_BYTES`): it
 * clears the ceiling at roughly 1.4 Mbit/s of usable upstream, which is below
 * anything an office connection produces, so a legitimate upload never hits it.
 * Sized for THIS ceiling — do not copy it to a helper with a different one.
 */
export const UPLOAD_DEADLINE_MS = 120_000;

async function runUpload(
  valuationRequestId: string,
  file: File,
  signal: AbortSignal,
): Promise<{ mediaFileId: string }> {
  const created = await apiClient.adminValuationRequests.createReportUpload({
    pathParams: { id: valuationRequestId },
    body: { contentType: file.type, sizeBytes: file.size, originalFilename: file.name },
  });

  if (!created.ok) {
    throw new DocumentUploadError();
  }

  const { fileId, upload } = created.body;

  const form = new FormData();

  for (const [name, value] of Object.entries(upload.fields)) {
    form.append(name, value);
  }

  form.append('file', file);

  const stored = await fetch(upload.url, { method: 'POST', body: form, signal });

  if (!stored.ok) {
    throw new DocumentUploadError();
  }

  const confirmed = await apiClient.adminValuationRequests.confirmReportUpload({
    pathParams: { id: valuationRequestId },
    body: { fileId },
  });

  if (!confirmed.ok) {
    throw new DocumentUploadError();
  }

  return { mediaFileId: confirmed.body.mediaFileId };
}

/**
 * Runs the operator valuation-report two-phase upload (IMM-407.5, per-slot since
 * IMM-475.2): presign via the admin `report-upload` leaf, POST the bytes straight
 * to object storage, then confirm. Unlike the agent's request-document upload this
 * writes NO document row — it returns the bare confirmed `{ mediaFileId }`, which
 * submit-for-approval holds on the request as one of its report pair. Throws
 * `DocumentPolicyError` for a file the slot does not accept or one over the 20 MB
 * ceiling (nothing is stored) and `DocumentUploadError` for any transport /
 * presign / confirm failure, including a round-trip that exceeds `UPLOAD_DEADLINE_MS`.
 */
export async function uploadValuationReport(
  valuationRequestId: string,
  file: File,
  accepted: ReadonlyArray<string>,
): Promise<{ mediaFileId: string }> {
  if (!isAllowedReport(file, accepted)) {
    throw new DocumentPolicyError();
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_DEADLINE_MS);
  // The deadline as a promise, so it also covers the presign and confirm legs —
  // they go through the generated client, which takes no per-call signal.
  const deadline = new Promise<never>((_, reject) => {
    controller.signal.addEventListener('abort', () => reject(new DocumentUploadError()));
  });
  // A deadline firing mid-confirm abandons the UI wait but cannot cancel the
  // in-flight confirm call (it takes no signal); the confirm can still succeed
  // server-side, leaving a confirmed mediaFileId nothing references. Harmless:
  // no document row is written here, and only submit-for-approval sets the
  // request's report pair.

  try {
    return await Promise.race([runUpload(valuationRequestId, file, controller.signal), deadline]);
  } finally {
    clearTimeout(timer);
  }
}
